import * as adminApi from '../api';

import { IPaginationParams } from '../../common';
import {
  IFetchAccountConnectionDataRequestData,
  IFetchDatastreamsDataRequestData,
  IGetAccountConnectionsResponseData,
  IGetCustomerReportsResponseData,
  IGetDatastreamsResponseData,
  IGetNotApprovedUsersResponseData,
  IGetUsersResponseData,
  IChangeUserStatusRequestData,
  IGetDatastreamFetchStatusRequestData,
  IGetDatastreamFetchStatusResponseData,
  InviteSupplierMarketplaceResponse,
  IGetCustomersWithFeaturesResponse,
  IUpdateCustomersFeaturesRequest,
  IUpdateCustomersFeaturesResponse,
  IGetCustomersWithPagesResponse,
  ICreateCustomPageRequest,
  ICreateCustomPageResponse,
  IEditCustomPageResponse,
  IEditCustomPageRequest,
  IToggleIsSynapseSyncEnabledResponse,
  IToggleIsSynapseSyncEnabledRequest,
  ISetSynapseDatabaseRequest,
  ISetSynapseDatabaseResponse,
  IGetSynapseDatabaseConnectionRequest,
  IGetSynapseDatabaseConnectionResponse,
  IGetSynapseViewsRequest,
  IGetSynapseViewsResponse,
  ISyncSynapseDatabaseViewsRequest,
  ISyncSynapseDatabaseViewsResponse,
  IGetClientsSynapseContainerLinkRequest,
  IGetClientsSynapseContainerLinkResponse,
  IGetCustomPagesFromFolderResponse,
  IGetCustomPagesFromFolderRequest,
  ISyncSynapseDatabaseViewsForAllCustomersRequest,
  IGetCustomersWithSideUsersResponse,
} from '../types';

// FE: bad pagination usage
export const getAccountConnections = async (
  params?: Partial<IPaginationParams>
): Promise<IGetAccountConnectionsResponseData> =>
  adminApi.getAccountConnections(
    params || {
      pageNumber: 1,
      pageSize: 1000,
    }
  );

export const fetchAccountConnectionData = async (
  data: IFetchAccountConnectionDataRequestData
): Promise<void> => adminApi.fetchAccountConnectionData(data);

export const deleteAccountConnection = async (
  accountId: string
): Promise<void> => adminApi.deleteAccountConnection(accountId);

// FE: bad pagination usage
export const getNotApprovedUsers = async (
  params?: Partial<IPaginationParams>
): Promise<IGetNotApprovedUsersResponseData> =>
  adminApi.getNotApprovedUsers(params || { pageSize: 10000, pageNumber: 1 });

export const approveUser = async (customerId: string): Promise<void> =>
  adminApi.approveUser(customerId);

export const changeUserStatus = async (
  body: IChangeUserStatusRequestData
): Promise<void> => adminApi.changeUserStatus(body);

// FE: bad pagination usage
export const getCustomerReports = async (
  params?: Partial<IPaginationParams>
): Promise<IGetCustomerReportsResponseData> =>
  adminApi.getCustomerReports({ pageSize: 10000, ...params });

export const getDatastreams = async (): Promise<IGetDatastreamsResponseData> =>
  adminApi.getDatastreams();

export const refreshDatastreams = async (): Promise<void> =>
  adminApi.refreshDatastreams();

export const getDatastreamFetchStatus = async (
  queryParams: IGetDatastreamFetchStatusRequestData
): Promise<IGetDatastreamFetchStatusResponseData> =>
  adminApi.getDatastreamFetchStatus(queryParams);

export const fetchDatastreamsData = async (
  data: IFetchDatastreamsDataRequestData
): Promise<void> => adminApi.fetchDatastreamsData(data);

export const getUsers = async (): Promise<IGetUsersResponseData> =>
  adminApi.getUsers();

export const inviteSupplierMarketplace = async (
  customerEmail: string
): Promise<InviteSupplierMarketplaceResponse> =>
  adminApi.inviteSupplierMarketplace(customerEmail);

export const getAllCustomersWithFeatures = async (
  pageNumber = 1,
  accumulatedCustomers: IGetCustomersWithFeaturesResponse['customersWithFeatures'] = [],
  totalCount?: number
): Promise<IGetCustomersWithFeaturesResponse> => {
  const response = await adminApi.getCustomersWithFeatures(100, pageNumber);
  accumulatedCustomers.push(...response.customersWithFeatures);

  const totalCustomersCount = totalCount ?? response.totalCustomersCount;

  if (accumulatedCustomers.length < totalCustomersCount) {
    return getAllCustomersWithFeatures(
      pageNumber + 1,
      accumulatedCustomers,
      totalCustomersCount
    );
  }

  return {
    customersWithFeatures: accumulatedCustomers,
    totalCustomersCount,
  };
};

export const updateCustomersFeatures = async (
  body: IUpdateCustomersFeaturesRequest
): Promise<IUpdateCustomersFeaturesResponse> =>
  adminApi.updateCustomersFeatures(body);

export const getCustomersWithPages =
  async (): Promise<IGetCustomersWithPagesResponse> =>
    adminApi.getCustomersWithPages();

export const getCustomPagesFromFolder = async (
  params: IGetCustomPagesFromFolderRequest
): Promise<IGetCustomPagesFromFolderResponse> =>
  adminApi.getCustomPagesFromFolder(params);

export const createCustomPage = async (
  body: ICreateCustomPageRequest
): Promise<ICreateCustomPageResponse> => adminApi.createCustomPage(body);

export const editCustomPage = async (
  body: IEditCustomPageRequest
): Promise<IEditCustomPageResponse> => adminApi.editCustomPage(body);

export const deleteCustomPage = async (pageId: string): Promise<void> =>
  adminApi.deleteCustomPage(pageId);

export const toggleIsSynapseSyncEnabled = async (
  body: IToggleIsSynapseSyncEnabledRequest
): Promise<IToggleIsSynapseSyncEnabledResponse> =>
  adminApi.toggleIsSynapseSyncEnabled(body);

export const setSynapseDatabase = async (
  body: ISetSynapseDatabaseRequest
): Promise<ISetSynapseDatabaseResponse> => adminApi.setSynapseDatabase(body);

export const testSynapseDatabaseConnection = async (
  queryParams: IGetSynapseDatabaseConnectionRequest
): Promise<IGetSynapseDatabaseConnectionResponse> =>
  adminApi.testSynapseDatabaseConnection(queryParams);

export const getSynapseDatabaseViews = async (
  queryParams: IGetSynapseViewsRequest
): Promise<IGetSynapseViewsResponse> =>
  adminApi.getSynapseDatabaseViews(queryParams);

export const syncSynapseDatabaseViews = async (
  body: ISyncSynapseDatabaseViewsRequest
): Promise<ISyncSynapseDatabaseViewsResponse> =>
  adminApi.syncSynapseDatabaseViews(body);

export const syncSynapseDatabaseViewsForAllCustomers = async (
  body: ISyncSynapseDatabaseViewsForAllCustomersRequest
): Promise<ISyncSynapseDatabaseViewsResponse> =>
  adminApi.syncSynapseDatabaseViewsForAllCustomers(body);

export const getClientsSynapseContainerLink = async (
  queryParams: IGetClientsSynapseContainerLinkRequest
): Promise<IGetClientsSynapseContainerLinkResponse> =>
  adminApi.getClientsSynapseContainerLink(queryParams);

export const getCustomersWithSideUsers =
  async (): Promise<IGetCustomersWithSideUsersResponse> =>
    adminApi.getCustomersWithSideUsers();
